<template>
  <section class="youtube-page">
    <header class="page-header">
      <div class="page-header__title-row">
        <h1 class="page-header__title">视频库</h1>
        <van-button round plain type="primary" size="small" class="filter-trigger" @click="openFilter">
          <span>{{ activeFilterLabel }}</span>
        </van-button>
      </div>
      <div v-if="selectedTag || selectedChannelId" class="filter-summary">
        <span v-if="selectedTag" class="filter-chip">标签: {{ selectedTag }}</span>
        <span v-if="selectedChannelId" class="filter-chip">频道: {{ activeChannelName }}</span>
      </div>
    </header>

    <div v-if="initialLoading" class="state-wrap">
      <van-loading size="24px" vertical>加载中...</van-loading>
    </div>

    <div v-else-if="initialError" class="state-wrap">
      <div class="state-card">
        <div class="state-card__title">加载失败</div>
        <div class="state-card__desc">{{ initialError }}</div>
        <van-button round type="primary" @click="reloadAll">重试</van-button>
      </div>
    </div>

    <template v-else>
      <div v-if="!list.length && finished" class="empty-wrap">
        <van-empty description="暂无视频数据" />
      </div>

      <van-list
        v-model:loading="loading"
        :finished="finished"
        :finished-text="finishedText"
        class="video-list"
        @load="onLoad"
      >
        <article
          v-for="item in list"
          :key="item.id"
          class="video-card"
          @click="openDetail(item.id)"
        >
          <div class="video-card__cover">
            <van-image width="100%" height="100%" fit="cover" :src="resolveVideoCover(item)" />
          </div>
          <div class="video-card__body">
            <div v-if="item.tag" class="video-card__tag">{{ item.tag }}</div>
            <h2 class="video-card__title">{{ item.titleCn || item.title || item.videoId || '-' }}</h2>
            <div v-if="item.titleCn && item.titleCn !== item.title" class="video-card__subtitle">
              {{ item.title || '-' }}
            </div>
            <div class="video-card__footer">
              <span class="video-card__views">{{ formatViewCount(item.viewCount) }}</span>
              <div class="video-card__meta">
                <span class="video-card__channel">{{ item.channelName || item.uploaderName || '-' }}</span>
                <span>{{ formatDate(item.publishedAt) }}</span>
              </div>
            </div>
          </div>
        </article>
      </van-list>
    </template>

    <van-popup
      v-model:show="filterVisible"
      position="bottom"
      round
      :style="{ height: '76%' }"
    >
      <div class="filter-panel">
        <div class="filter-panel__header">
          <div>
            <div class="filter-panel__eyebrow">Filter</div>
            <div class="filter-panel__title">筛选视频</div>
          </div>
          <van-button round plain size="small" @click="filterVisible = false">关闭</van-button>
        </div>

        <div class="filter-section">
          <div class="filter-section__title">标签</div>
          <van-radio-group v-model="draftTag" class="filter-group">
            <van-cell-group inset>
              <van-cell title="全部标签" clickable @click="draftTag = ''">
                <template #right-icon>
                  <van-radio name="" />
                </template>
              </van-cell>
              <van-cell
                v-for="item in tags"
                :key="item.tag"
                :title="item.tag"
                :label="`${item.count} 条视频`"
                clickable
                @click="draftTag = item.tag"
              >
                <template #right-icon>
                  <van-radio :name="item.tag" />
                </template>
              </van-cell>
            </van-cell-group>
          </van-radio-group>
        </div>

        <div class="filter-section">
          <div class="filter-section__title">频道</div>
          <van-radio-group v-model="draftChannelId" class="filter-group">
            <van-cell-group inset>
              <van-cell title="全部频道" clickable @click="draftChannelId = ''">
                <template #right-icon>
                  <van-radio name="" />
                </template>
              </van-cell>
              <van-cell
                v-for="channel in channels"
                :key="channel.channelId"
                :title="channel.channelName"
                :label="`${channel.count} 条视频`"
                clickable
                @click="draftChannelId = channel.channelId"
              >
                <template #right-icon>
                  <van-radio :name="channel.channelId" />
                </template>
              </van-cell>
            </van-cell-group>
          </van-radio-group>
        </div>

        <div class="filter-panel__actions">
          <van-button round plain block @click="clearFilter">清空</van-button>
          <van-button round type="primary" block @click="applyFilter">确认</van-button>
        </div>
      </div>
    </van-popup>
  </section>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { fetchYoutubeChannels, fetchYoutubeList, fetchYoutubeTags } from '../api/youtube'
import { formatDate, formatViewCount, resolveVideoCover } from '../utils/youtube'

const PAGE_SIZE = 10

const route = useRoute()
const router = useRouter()

const initialLoading = ref(true)
const initialError = ref('')
const loading = ref(false)
const requesting = ref(false)
const finished = ref(false)
const page = ref(1)
const list = ref([])
const channels = ref([])
const tags = ref([])
const filterVisible = ref(false)
const selectedChannelId = ref(String(route.query.channelId || ''))
const selectedTag = ref(String(route.query.tag || ''))
const draftChannelId = ref(selectedChannelId.value)
const draftTag = ref(selectedTag.value)

const activeChannelName = computed(() => {
  if (!selectedChannelId.value) return '全部频道'
  const matched = channels.value.find((item) => item.channelId === selectedChannelId.value)
  return matched?.channelName || '频道筛选'
})

const activeFilterLabel = computed(() => {
  if (selectedTag.value) return `标签: ${selectedTag.value}`
  if (selectedChannelId.value) return activeChannelName.value
  return '全部视频'
})

const finishedText = computed(() => {
  if (!list.value.length) return ''
  return '没有更多了'
})

watch(
  () => [route.query.channelId, route.query.tag],
  async ([channelId, tag]) => {
    const nextChannelId = String(channelId || '')
    const nextTag = String(tag || '')
    const changed = nextChannelId !== selectedChannelId.value || nextTag !== selectedTag.value

    selectedChannelId.value = nextChannelId
    selectedTag.value = nextTag
    draftChannelId.value = nextChannelId
    draftTag.value = nextTag

    if (!initialLoading.value && changed) {
      await reloadList()
    }
  },
)

onMounted(async () => {
  await reloadAll()
})

async function reloadAll() {
  initialLoading.value = true
  initialError.value = ''

  try {
    const [channelData, tagData] = await Promise.all([
      fetchYoutubeChannels(),
      fetchYoutubeTags(),
    ])
    channels.value = Array.isArray(channelData) ? channelData : []
    tags.value = Array.isArray(tagData) ? tagData : []
    await reloadList({ force: true })
  } catch (error) {
    initialError.value = error?.message || '加载失败'
  } finally {
    initialLoading.value = false
  }
}

function openFilter() {
  draftChannelId.value = selectedChannelId.value
  draftTag.value = selectedTag.value
  filterVisible.value = true
}

async function reloadList(options = {}) {
  page.value = 1
  finished.value = false
  list.value = []
  await loadPage(options)
}

async function onLoad() {
  await loadPage()
}

async function loadPage(options = {}) {
  const force = options.force === true
  if ((!force && initialLoading.value) || requesting.value || finished.value) return

  requesting.value = true
  loading.value = true
  initialError.value = ''

  try {
    const data = await fetchYoutubeList({
      page: page.value,
      pageSize: PAGE_SIZE,
      channelId: selectedChannelId.value || undefined,
      tag: selectedTag.value || undefined,
      sortBy: 'published_at_desc',
    })

    const nextList = Array.isArray(data?.list) ? data.list : []
    const paginationTotal = Number(data?.pagination?.total || 0)

    list.value = page.value === 1 ? nextList : list.value.concat(nextList)
    page.value += 1
    finished.value = list.value.length >= paginationTotal || nextList.length < PAGE_SIZE
  } catch (error) {
    if (!list.value.length) {
      initialError.value = error?.message || '加载失败'
    }
    finished.value = true
  } finally {
    requesting.value = false
    loading.value = false
  }
}

async function applyFilter() {
  filterVisible.value = false
  const nextChannelId = draftChannelId.value
  const nextTag = draftTag.value
  const currentChannelId = String(route.query.channelId || '')
  const currentTag = String(route.query.tag || '')
  const nextQuery = {}

  if (nextChannelId) {
    nextQuery.channelId = nextChannelId
  }
  if (nextTag) {
    nextQuery.tag = nextTag
  }

  await router.replace({
    name: 'youtube-list',
    query: nextQuery,
  })

  if (currentChannelId === nextChannelId && currentTag === nextTag) {
    selectedChannelId.value = nextChannelId
    selectedTag.value = nextTag
    draftChannelId.value = nextChannelId
    draftTag.value = nextTag
    await reloadList()
  }
}

function clearFilter() {
  draftChannelId.value = ''
  draftTag.value = ''
  return applyFilter()
}

function openDetail(id) {
  if (!id) return
  const query = {
    id: String(id),
  }
  if (selectedChannelId.value) {
    query.channelId = selectedChannelId.value
  }
  if (selectedTag.value) {
    query.tag = selectedTag.value
  }
  router.push({
    name: 'youtube-detail',
    query,
  })
}
</script>

<style scoped>
.youtube-page {
  min-height: 100vh;
  padding: calc(env(safe-area-inset-top) + 16px) 16px 20px;
  box-sizing: border-box;
}

.page-header {
  margin-bottom: 16px;
}

.page-header__title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.page-header__title {
  margin: 0;
  color: var(--yt-text);
  font-size: 30px;
  line-height: 1.1;
  letter-spacing: -0.06em;
}

.filter-trigger {
  flex-shrink: 0;
}

.filter-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.filter-chip {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(255, 106, 61, 0.12);
  color: var(--yt-accent);
  font-size: 12px;
  font-weight: 600;
}

.state-wrap,
.empty-wrap {
  padding-top: 72px;
}

.state-card {
  padding: 28px 22px;
  text-align: center;
  border: 1px solid var(--yt-line);
  border-radius: var(--yt-radius-xl);
  background: var(--yt-surface-strong);
  box-shadow: var(--yt-shadow);
}

.state-card__title {
  color: var(--yt-text);
  font-size: 18px;
  font-weight: 700;
}

.state-card__desc {
  margin: 14px 0 18px;
  color: var(--yt-muted);
  font-size: 14px;
  line-height: 1.8;
  white-space: pre-wrap;
  word-break: break-word;
}

.video-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.video-card {
  display: grid;
  grid-template-columns: 132px minmax(0, 1fr);
  gap: 14px;
  padding: 12px;
  border: 1px solid var(--yt-line);
  border-radius: var(--yt-radius-xl);
  background: var(--yt-surface-strong);
  box-shadow: var(--yt-shadow);
}

.video-card__cover {
  overflow: hidden;
  border-radius: calc(var(--yt-radius-xl) - 6px);
  aspect-ratio: 16 / 9;
  background: rgba(148, 163, 184, 0.18);
}

.video-card__body {
  min-width: 0;
}

.video-card__tag {
  display: inline-flex;
  align-items: center;
  margin-bottom: 8px;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.06);
  color: var(--yt-muted);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.video-card__title {
  margin: 0;
  color: var(--yt-text);
  font-size: 17px;
  line-height: 1.45;
  word-break: break-word;
}

.video-card__subtitle {
  margin-top: 8px;
  color: var(--yt-muted);
  font-size: 13px;
  line-height: 1.65;
  word-break: break-word;
}

.video-card__footer {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-top: 14px;
}

.video-card__views {
  color: var(--yt-accent);
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
}

.video-card__meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  min-width: 0;
  color: var(--yt-muted);
  font-size: 12px;
  text-align: right;
}

.video-card__channel {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.filter-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 20px 16px calc(env(safe-area-inset-bottom) + 20px);
  box-sizing: border-box;
  overflow: hidden;
}

.filter-panel__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.filter-panel__eyebrow {
  color: var(--yt-accent);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.filter-panel__title {
  margin-top: 6px;
  color: var(--yt-text);
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.05em;
}

.filter-section {
  margin-top: 18px;
}

.filter-section__title {
  margin: 0 4px 10px;
  color: var(--yt-muted);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.filter-group {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding-bottom: 16px;
}

.filter-panel__actions {
  position: sticky;
  bottom: 0;
  z-index: 2;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 0;
  padding-top: 18px;
  background: linear-gradient(180deg, rgba(248, 250, 252, 0) 0%, #f8fafc 42%);
}

@media (max-width: 360px) {
  .video-card {
    grid-template-columns: 1fr;
  }

  .video-card__meta {
    align-items: flex-start;
    text-align: left;
  }

  .video-card__footer {
    flex-direction: column;
  }

  .filter-panel__actions {
    grid-template-columns: 1fr;
  }
}
</style>
